import { format, isSameDay } from "date-fns";
import * as Dfns from "date-fns/fp";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as uuid from "uuid";
import {
  LLMMessage,
  LLMSession,
  SendPromptArgs,
  SendPromptResponse,
  SessionId
} from "@blisspointmedia/bpm-types/dist/LLM";
import FloatingPanel from "../../Components/FloatingPanel/FloatingPanel";
import { useCompanyInfo } from "../../redux/company";
import { useSetError } from "../../redux/modals";
import * as UserRedux from "../../redux/user";
import { awaitJSON, fetchCometLambda, MiscLambdaFetch } from "../../utils/fetch-utils";
import { useMap } from "../../utils/hooks/useData";
import LLMMainPanel from "./LLMMainPanel";
import LLMSidePanel from "./LLMSidePanel";

interface LLMPanelProps {
  show: boolean;
  onHide: () => void;
}

const createEmptySession = (userEmail, client): LLMSession => {
  const id = uuid.v4();

  const now = new Date();

  return {
    sessionId: id,
    user: userEmail,
    client: client,
    timestamp: now.getTime(),
    state: null,
    sessionName: Dfns.format("EEEE MMM do h:mmaaa", now),
  };
};

const LLMPanel = ({ show, onHide }: LLMPanelProps): JSX.Element => {
  const userEmail = useSelector(UserRedux.emailSelector) as string;
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const [showSidePanel, setShowSidePanel] = useState<boolean>(true);
  const [sessions, setSessions] = useState<Record<SessionId, LLMSession> | undefined>();
  const [currentSessionId, setCurrentSessionId] = useState<SessionId | undefined>();
  const currentSession = useMemo(
    () => (sessions && currentSessionId ? sessions[currentSessionId] : undefined),
    [currentSessionId, sessions]
  );
  const [messageMap, setSessionMessages, setMessageMap] = useMap<SessionId, LLMMessage[]>();
  const [draftMessage, setDraftMessage] = useState("");
  const [submittingSessions, setSubmittingSessions] = useState(new Set());
  const submitting = useMemo(() => submittingSessions.has(currentSession?.sessionId), [
    currentSession?.sessionId,
    submittingSessions,
  ]);

  // When messages is an empty array, we expect the New Chat interface
  // When messages is nullish, we expect to be in-progress fetching the chat history from the backend
  const messages: LLMMessage[] | undefined = useMemo(
    () => (currentSession ? messageMap[currentSession.sessionId] : []),
    [currentSession, messageMap]
  );

  const fetchConversation = useCallback(
    async (sessionId: string) => {
      let res = await MiscLambdaFetch("/getLLMConversation", {
        method: "GET",
        params: { sessionId: sessionId },
      });
      const { messages: messagesRes } = await awaitJSON<{ messages: LLMMessage[] }>(res);
      setSessionMessages(sessionId, messagesRes);
    },
    [setSessionMessages]
  );

  const selectSession = useCallback(
    sessionId => {
      if (!sessions) {
        return;
      }

      setCurrentSessionId(sessionId);
      setShowSidePanel(false);

      if (!messageMap[sessionId]) {
        fetchConversation(sessionId);
      }
    },
    [fetchConversation, messageMap, sessions]
  );

  const startNewSession = useCallback(() => {
    const newSession = createEmptySession(userEmail, cid);
    const { sessionId: newSessionId } = newSession;
    setSessions(prev => ({ ...prev, [newSessionId]: newSession }));
    setCurrentSessionId(newSessionId);
    setSessionMessages(newSessionId, []);
    setShowSidePanel(false);
  }, [cid, setSessionMessages, userEmail]);

  useEffect(() => {
    const fetchSessions = async () => {
      let res = await MiscLambdaFetch("/getLLMSessions", {
        method: "GET",
        params: { user: userEmail, client: cid },
      });
      let { sessions: sessionsRes } = await awaitJSON<{ sessions: LLMSession[] }>(res);
      const sessionsMap = sessionsRes.reduce(
        (acc, session) => ({
          ...acc,
          [session.sessionId]: session,
        }),
        {}
      );
      await setSessions(sessionsMap);

      // If the user has existing conversations, and the most recent one is today, pre-select it
      // Otherwise, initialize a new session
      if (sessionsRes.length > 0) {
        const { timestamp: firstSessionTimestamp, sessionId: firstSessionId } = sessionsRes[0];
        const isSameDaySession = isSameDay(new Date(), firstSessionTimestamp);
        if (isSameDaySession) {
          setCurrentSessionId(firstSessionId);
          fetchConversation(firstSessionId);
          return;
        }
      }

      startNewSession();
    };

    if (show && !sessions) {
      fetchSessions();
    }
  }, [cid, currentSession, fetchConversation, selectSession, sessions, show, startNewSession, userEmail]);

  const addMessage = (newMessage, sessionId: SessionId) => {
    setMessageMap(prev => {
      const existingMessages = prev[sessionId] ?? [];
      return { ...prev, [sessionId]: [...existingMessages, newMessage] };
    });
  };

  const sendPrompt = async message => {
    if (!currentSession || !messages) {
      throw new Error("Cannot sendPrompt with undefined currentSession or messages");
    }

    const isNewSession = messages.length === 0;

    if (!submitting) {
      // Capture the sessionId at the start of the request
      const { sessionId } = currentSession;
      setSubmittingSessions(current => new Set(current).add(sessionId));
      // ISO 8601 Timestamp + timezone offset
      const now = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
      const input: SendPromptArgs = {
        prompt: message,
        sessionId,
        client: cid,
        userEmail,
        isNewSession,
        now,
      };
      addMessage(
        {
          type: "PROMPT",
          timestamp: new Date().getTime(),
          contents: message,
        },
        sessionId
      );

      try {
        const { message, sessionName } = await fetchCometLambda<SendPromptArgs, SendPromptResponse>(
          {
            service: MiscLambdaFetch,
            standardEndpoint: "/sendLLMPrompt",
            cometLambdaName: "llm-sendPrompt",
            input,
            method: "post",
            noTimeout: true,
          }
        );

        if (sessionName) {
          if (isNewSession) {
            setSessions(current => ({
              ...current,
              [sessionId]: { ...currentSession, sessionName },
            }));
          }
        }

        addMessage(
          {
            type: "RESPONSE",
            timestamp: new Date().getTime(),
            contents: message,
          },
          sessionId
        );
      } catch (e: any) {
        setError({ message: e.message });
      } finally {
        setSubmittingSessions(current => {
          const next = new Set(current);
          next.delete(sessionId);
          return next;
        });
      }
    }
  };

  const updateMessage = useCallback(
    (
      sessionId: SessionId | undefined,
      updatedMsg: Pick<LLMMessage, "messageId"> & Partial<Omit<LLMMessage, "messageId">>
    ) => {
      if (!sessionId) {
        return;
      }

      const existingMessages = messageMap[sessionId] ?? [];
      const messageIndex = existingMessages.findIndex(
        msg => msg.messageId === updatedMsg.messageId
      );

      if (messageIndex === -1) {
        return;
      }

      const updatedMessages = [...existingMessages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        ...updatedMsg,
      };

      setSessionMessages(sessionId, updatedMessages);
    },
    [messageMap, setSessionMessages]
  );

  return (
    <FloatingPanel
      id="llm-floating-panel"
      show={show}
      onHide={onHide}
      showSidePanel={showSidePanel}
      setShowSidePanel={setShowSidePanel}
      leftActions={<LLMMainPanel.LeftActions startNewChat={startNewSession} />}
      body={
        <LLMMainPanel.Body
          isOpen={show}
          messages={messages}
          sessionId={currentSession?.sessionId}
          updateMessage={updateMessage}
          submitting={submitting}
          setDraftMessage={setDraftMessage}
        />
      }
      footer={
        <LLMMainPanel.Footer
          isOpen={show}
          sendPrompt={sendPrompt}
          submitting={submitting}
          draftMessage={draftMessage}
          setDraftMessage={setDraftMessage}
        />
      }
      sidePanelBody={
        <LLMSidePanel.Body
          sessions={sessions}
          setSessions={setSessions}
          currentSessionId={currentSession?.sessionId}
          selectSession={selectSession}
          startNewSession={startNewSession}
        />
      }
      sidePanelFooter={<LLMSidePanel.Footer />}
      minWidth={342}
      maxWidth={1440}
      initialWidth={465}
      minHeight={396}
      initialHeight={745}
      initialPosition={{
        xDist: 64,
        xDirection: "right",
        yDist: 64,
        yDirection: "bottom",
      }}
    />
  );
};

export default LLMPanel;
